import React, {useState, useEffect} from 'react';
import {useNavigate} from 'react-router-dom';

export function ProfileEditor() {
    const navigate = useNavigate();
    const [profileData, setProfileData] = useState(() => {
        const saved = localStorage.getItem('koenig-profile-data');
        if (saved) {
            return JSON.parse(saved);
        }
        return {
            name: '',
            tagline: '',
            currentStatus: '',
            bio: '',
            faqItems: [{question: '', answer: ''}],
            sponsorshipInfo: '',
            email: '',
            profileImage: null
        };
    });
    const [isSaving, setIsSaving] = useState(false);
    const [linkCopied, setLinkCopied] = useState(false);

    const handleInputChange = (field, value) => {
        setProfileData(prev => ({...prev, [field]: value}));
    };

    const handleFaqChange = (index, field, value) => {
        const newFaqs = [...profileData.faqItems];
        newFaqs[index] = {...newFaqs[index], [field]: value};
        setProfileData(prev => ({...prev, faqItems: newFaqs}));
    };

    const addFaqItem = () => {
        setProfileData(prev => ({
            ...prev,
            faqItems: [...prev.faqItems, {question: '', answer: ''}]
        }));
    };

    const removeFaqItem = (index) => {
        setProfileData(prev => ({
            ...prev,
            faqItems: prev.faqItems.filter((_, i) => i !== index)
        }));
    };

    const handleSave = () => {
        setIsSaving(true);
        // Save to localStorage
        localStorage.setItem('koenig-profile-data', JSON.stringify(profileData));

        setTimeout(() => {
            setIsSaving(false);
            alert('Profile saved successfully!');
        }, 500);
    };

    const handlePreview = () => {
        // Save first, then navigate to profile
        localStorage.setItem('koenig-profile-data', JSON.stringify(profileData));
        navigate('/profile');
    };

    const copyProfileLink = async () => {
        const profileUrl = `${window.location.origin}/profile`;
        try {
            await navigator.clipboard.writeText(profileUrl);
            setLinkCopied(true);
            setTimeout(() => setLinkCopied(false), 3000);
        } catch (err) {
            console.error('Failed to copy:', err);
            alert('Failed to copy link');
        }
    };

    return (
        <div style={styles.container}>
            {/* Header */}
            <div style={styles.header}>
                <div style={styles.headerLeft}>
                    <button onClick={() => navigate('/')} style={styles.backButton}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M19 12H5M12 19l-7-7 7-7"/>
                        </svg>
                        Back to Editor
                    </button>
                </div>
                <h1 style={styles.headerTitle}>Edit Your Profile</h1>
                <div style={styles.headerRight}>
                    <button onClick={copyProfileLink} style={styles.copyLinkButton}>
                        {linkCopied ? (
                            <>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M20 6L9 17l-5-5"/>
                                </svg>
                                Copied!
                            </>
                        ) : (
                            <>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                                </svg>
                                Copy Profile Link
                            </>
                        )}
                    </button>
                    <button onClick={handlePreview} style={styles.previewButton}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                            <circle cx="12" cy="12" r="3"/>
                        </svg>
                        Preview Profile
                    </button>
                    <button onClick={handleSave} style={styles.saveButton} disabled={isSaving}>
                        {isSaving ? 'Saving...' : 'Save Profile'}
                    </button>
                </div>
            </div>

            {/* Main Content */}
            <div style={styles.content}>
                <div style={styles.formContainer}>
                    {/* Profile Image Section */}
                    <div style={styles.section}>
                        <h2 style={styles.sectionTitle}>Profile Image</h2>
                        <div style={styles.profileImageSection}>
                            <div style={styles.profileImagePreview}>
                                {profileData.profileImage ? (
                                    <img src={profileData.profileImage} alt="Profile" style={styles.profileImage} />
                                ) : (
                                    <div style={styles.defaultAvatar}>
                                        <svg width="40" height="40" viewBox="0 0 32 32">
                                            <defs>
                                                <linearGradient id="avatar-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                                                    <stop offset="0%" style={{stopColor: '#667EEA'}} />
                                                    <stop offset="100%" style={{stopColor: '#764BA2'}} />
                                                </linearGradient>
                                            </defs>
                                            <rect width="32" height="32" rx="16" fill="url(#avatar-grad)"/>
                                            <circle cx="16" cy="12" r="5" fill="#FFFFFF" opacity="0.9"/>
                                            <path d="M16 19c-5 0-9 2.5-9 5.5v1.5h18v-1.5c0-3-4-5.5-9-5.5z" fill="#FFFFFF" opacity="0.9"/>
                                        </svg>
                                    </div>
                                )}
                            </div>
                            <div style={styles.profileImageActions}>
                                <input
                                    type="url"
                                    placeholder="Enter image URL..."
                                    value={profileData.profileImage || ''}
                                    onChange={(e) => handleInputChange('profileImage', e.target.value)}
                                    style={styles.input}
                                />
                                <p style={styles.helpText}>Enter a URL to your profile image</p>
                            </div>
                        </div>
                    </div>

                    {/* Basic Info Section */}
                    <div style={styles.section}>
                        <h2 style={styles.sectionTitle}>Basic Information</h2>

                        <div style={styles.formGroup}>
                            <label style={styles.label}>Name / Headline</label>
                            <input
                                type="text"
                                placeholder="e.g., Alex (The Silly Point) - Decoding cricket strategy"
                                value={profileData.name}
                                onChange={(e) => handleInputChange('name', e.target.value)}
                                style={styles.input}
                            />
                            <p style={styles.helpText}>This appears as your main headline on your profile</p>
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.label}>Tagline (optional)</label>
                            <input
                                type="text"
                                placeholder="A short description about you..."
                                value={profileData.tagline}
                                onChange={(e) => handleInputChange('tagline', e.target.value)}
                                style={styles.input}
                            />
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.label}>Contact Email</label>
                            <input
                                type="email"
                                placeholder="your@email.com"
                                value={profileData.email}
                                onChange={(e) => handleInputChange('email', e.target.value)}
                                style={styles.input}
                            />
                        </div>
                    </div>

                    {/* Current Status Section */}
                    <div style={styles.section}>
                        <h2 style={styles.sectionTitle}>Current Status</h2>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>What are you working on?</label>
                            <textarea
                                placeholder="**Current Status:** Covering the Ashes Test (Day 3). Writing a deep dive on Stokes' captaincy."
                                value={profileData.currentStatus}
                                onChange={(e) => handleInputChange('currentStatus', e.target.value)}
                                style={styles.textarea}
                                rows={3}
                            />
                            <p style={styles.helpText}>You can use **bold** for emphasis</p>
                        </div>
                    </div>

                    {/* FAQ Section */}
                    <div style={styles.section}>
                        <h2 style={styles.sectionTitle}>FAQ</h2>
                        <p style={styles.sectionDescription}>Add frequently asked questions that visitors might have</p>

                        {profileData.faqItems.map((faq, index) => (
                            <div key={index} style={styles.faqItem}>
                                <div style={styles.faqHeader}>
                                    <span style={styles.faqNumber}>Question {index + 1}</span>
                                    {profileData.faqItems.length > 1 && (
                                        <button
                                            onClick={() => removeFaqItem(index)}
                                            style={styles.removeFaqButton}
                                        >
                                            Remove
                                        </button>
                                    )}
                                </div>
                                <div style={styles.formGroup}>
                                    <input
                                        type="text"
                                        placeholder="e.g., Do you cover IPL?"
                                        value={faq.question}
                                        onChange={(e) => handleFaqChange(index, 'question', e.target.value)}
                                        style={styles.input}
                                    />
                                </div>
                                <div style={styles.formGroup}>
                                    <textarea
                                        placeholder="Your answer..."
                                        value={faq.answer}
                                        onChange={(e) => handleFaqChange(index, 'answer', e.target.value)}
                                        style={styles.textarea}
                                        rows={2}
                                    />
                                </div>
                            </div>
                        ))}

                        <button onClick={addFaqItem} style={styles.addFaqButton}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M12 5v14M5 12h14"/>
                            </svg>
                            Add FAQ Item
                        </button>
                    </div>

                    {/* Sponsorship Section */}
                    <div style={styles.section}>
                        <h2 style={styles.sectionTitle}>Sponsorships</h2>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>Sponsorship Information</label>
                            <textarea
                                placeholder="e.g., I charge $500 per slot. My audience is 80% UK/India. Email me at alex@cricket.com."
                                value={profileData.sponsorshipInfo}
                                onChange={(e) => handleInputChange('sponsorshipInfo', e.target.value)}
                                style={styles.textarea}
                                rows={3}
                            />
                        </div>
                    </div>

                    {/* Bio Section */}
                    <div style={styles.section}>
                        <h2 style={styles.sectionTitle}>Bio / About (optional)</h2>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>Tell visitors more about yourself</label>
                            <textarea
                                placeholder="Write a longer bio here..."
                                value={profileData.bio}
                                onChange={(e) => handleInputChange('bio', e.target.value)}
                                style={{...styles.textarea, minHeight: '150px'}}
                                rows={6}
                            />
                        </div>
                    </div>

                    {/* Save Actions */}
                    <div style={styles.actionBar}>
                        <button onClick={handlePreview} style={styles.previewButtonLarge}>
                            Preview Profile
                        </button>
                        <button onClick={handleSave} style={styles.saveButtonLarge} disabled={isSaving}>
                            {isSaving ? 'Saving...' : 'Save Profile'}
                        </button>
                    </div>
                </div>

                {/* Live Preview Panel */}
                <div style={styles.previewPanel}>
                    <h3 style={styles.previewTitle}>Live Preview</h3>
                    <div style={styles.previewContent}>
                        <div style={styles.previewProfile}>
                            {/* Profile Image */}
                            <div style={styles.previewAvatar}>
                                {profileData.profileImage ? (
                                    <img src={profileData.profileImage} alt="" style={{width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover'}} />
                                ) : (
                                    <svg width="32" height="32" viewBox="0 0 32 32">
                                        <defs>
                                            <linearGradient id="preview-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                                                <stop offset="0%" style={{stopColor: '#667EEA'}} />
                                                <stop offset="100%" style={{stopColor: '#764BA2'}} />
                                            </linearGradient>
                                        </defs>
                                        <rect width="32" height="32" rx="16" fill="url(#preview-grad)"/>
                                        <circle cx="16" cy="12" r="4" fill="#FFFFFF" opacity="0.9"/>
                                        <path d="M16 18c-4 0-7 2-7 4v2h14v-2c0-2-3-4-7-4z" fill="#FFFFFF" opacity="0.9"/>
                                    </svg>
                                )}
                            </div>

                            {/* Name */}
                            <h4 style={styles.previewName}>
                                {profileData.name || 'Your Name'}
                            </h4>

                            {/* Tagline */}
                            {profileData.tagline && (
                                <p style={styles.previewTagline}>{profileData.tagline}</p>
                            )}

                            {/* Status */}
                            {profileData.currentStatus && (
                                <div style={styles.previewStatus}>
                                    <span>✍️</span>
                                    <span dangerouslySetInnerHTML={{__html: profileData.currentStatus.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')}} />
                                </div>
                            )}

                            {/* FAQ Preview */}
                            {profileData.faqItems.some(f => f.question) && (
                                <div style={styles.previewFaq}>
                                    <h5 style={styles.previewFaqTitle}>FAQ</h5>
                                    {profileData.faqItems.filter(f => f.question).map((faq, idx) => (
                                        <div key={idx} style={styles.previewFaqItem}>
                                            <p style={styles.previewQuestion}>{faq.question}</p>
                                            <p style={styles.previewAnswer}>{faq.answer}</p>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

const styles = {
    container: {
        minHeight: '100vh',
        backgroundColor: '#f5f5f5',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    },
    header: {
        position: 'sticky',
        top: 0,
        zIndex: 100,
        backgroundColor: '#fff',
        borderBottom: '1px solid #e5e5e5',
        padding: '16px 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
    },
    headerLeft: {
        display: 'flex',
        alignItems: 'center'
    },
    backButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px 12px',
        background: 'transparent',
        border: 'none',
        color: '#6b7280',
        fontSize: '14px',
        cursor: 'pointer',
        borderRadius: '6px',
        transition: 'all 0.2s'
    },
    headerTitle: {
        fontSize: '18px',
        fontWeight: 600,
        color: '#1f2937',
        margin: 0
    },
    headerRight: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
    },
    copyLinkButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '8px 16px',
        background: '#fff',
        border: '1px solid #e5e5e5',
        borderRadius: '8px',
        fontSize: '14px',
        color: '#374151',
        cursor: 'pointer',
        transition: 'all 0.2s'
    },
    previewButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '8px 16px',
        background: '#fff',
        border: '1px solid #e5e5e5',
        borderRadius: '8px',
        fontSize: '14px',
        color: '#374151',
        cursor: 'pointer',
        transition: 'all 0.2s'
    },
    saveButton: {
        padding: '8px 20px',
        background: '#667EEA',
        border: 'none',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: 600,
        color: '#fff',
        cursor: 'pointer',
        transition: 'all 0.2s'
    },
    content: {
        display: 'flex',
        gap: '24px',
        padding: '24px',
        maxWidth: '1400px',
        margin: '0 auto'
    },
    formContainer: {
        flex: 1,
        maxWidth: '700px'
    },
    section: {
        backgroundColor: '#fff',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
    },
    sectionTitle: {
        fontSize: '18px',
        fontWeight: 600,
        color: '#1f2937',
        margin: '0 0 8px 0'
    },
    sectionDescription: {
        fontSize: '14px',
        color: '#6b7280',
        margin: '0 0 20px 0'
    },
    formGroup: {
        marginBottom: '16px'
    },
    label: {
        display: 'block',
        fontSize: '14px',
        fontWeight: 500,
        color: '#374151',
        marginBottom: '6px'
    },
    input: {
        width: '100%',
        padding: '12px 14px',
        border: '1px solid #e5e5e5',
        borderRadius: '8px',
        fontSize: '14px',
        outline: 'none',
        transition: 'border-color 0.2s',
        boxSizing: 'border-box'
    },
    textarea: {
        width: '100%',
        padding: '12px 14px',
        border: '1px solid #e5e5e5',
        borderRadius: '8px',
        fontSize: '14px',
        outline: 'none',
        resize: 'vertical',
        fontFamily: 'inherit',
        transition: 'border-color 0.2s',
        boxSizing: 'border-box'
    },
    helpText: {
        fontSize: '12px',
        color: '#9ca3af',
        marginTop: '4px'
    },
    profileImageSection: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '20px',
        marginTop: '16px'
    },
    profileImagePreview: {
        width: '80px',
        height: '80px',
        borderRadius: '50%',
        overflow: 'hidden',
        flexShrink: 0,
        backgroundColor: '#f3f4f6',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    profileImage: {
        width: '100%',
        height: '100%',
        objectFit: 'cover'
    },
    defaultAvatar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    profileImageActions: {
        flex: 1
    },
    faqItem: {
        backgroundColor: '#f9fafb',
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '12px'
    },
    faqHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '12px'
    },
    faqNumber: {
        fontSize: '13px',
        fontWeight: 600,
        color: '#6b7280'
    },
    removeFaqButton: {
        padding: '4px 8px',
        background: 'transparent',
        border: 'none',
        color: '#ef4444',
        fontSize: '13px',
        cursor: 'pointer'
    },
    addFaqButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '10px 16px',
        background: '#f3f4f6',
        border: '1px dashed #d1d5db',
        borderRadius: '8px',
        fontSize: '14px',
        color: '#6b7280',
        cursor: 'pointer',
        width: '100%',
        justifyContent: 'center',
        transition: 'all 0.2s'
    },
    actionBar: {
        display: 'flex',
        gap: '12px',
        justifyContent: 'flex-end',
        paddingTop: '16px'
    },
    previewButtonLarge: {
        padding: '12px 24px',
        background: '#fff',
        border: '1px solid #e5e5e5',
        borderRadius: '8px',
        fontSize: '15px',
        fontWeight: 500,
        color: '#374151',
        cursor: 'pointer',
        transition: 'all 0.2s'
    },
    saveButtonLarge: {
        padding: '12px 32px',
        background: '#667EEA',
        border: 'none',
        borderRadius: '8px',
        fontSize: '15px',
        fontWeight: 600,
        color: '#fff',
        cursor: 'pointer',
        transition: 'all 0.2s'
    },
    previewPanel: {
        width: '400px',
        flexShrink: 0,
        position: 'sticky',
        top: '100px',
        height: 'fit-content'
    },
    previewTitle: {
        fontSize: '14px',
        fontWeight: 600,
        color: '#6b7280',
        margin: '0 0 12px 0',
        textTransform: 'uppercase',
        letterSpacing: '0.5px'
    },
    previewContent: {
        backgroundColor: '#fff',
        borderRadius: '12px',
        boxShadow: '0 4px 24px rgba(0,0,0,0.1)',
        overflow: 'hidden'
    },
    previewProfile: {
        padding: '32px 24px',
        textAlign: 'center'
    },
    previewAvatar: {
        width: '72px',
        height: '72px',
        borderRadius: '50%',
        margin: '0 auto 16px',
        overflow: 'hidden',
        backgroundColor: '#f3f4f6',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    previewName: {
        fontSize: '20px',
        fontWeight: 700,
        color: '#1f2937',
        margin: '0 0 8px 0',
        lineHeight: 1.3
    },
    previewTagline: {
        fontSize: '14px',
        color: '#6b7280',
        margin: '0 0 16px 0'
    },
    previewStatus: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '8px',
        backgroundColor: '#e8f5e9',
        padding: '12px 16px',
        borderRadius: '8px',
        fontSize: '13px',
        color: '#1f2937',
        textAlign: 'left',
        marginBottom: '16px'
    },
    previewFaq: {
        textAlign: 'left',
        marginTop: '20px',
        paddingTop: '20px',
        borderTop: '1px solid #e5e5e5'
    },
    previewFaqTitle: {
        fontSize: '16px',
        fontWeight: 600,
        color: '#1f2937',
        margin: '0 0 12px 0'
    },
    previewFaqItem: {
        marginBottom: '12px'
    },
    previewQuestion: {
        fontSize: '13px',
        fontWeight: 500,
        color: '#1f2937',
        margin: '0 0 4px 0'
    },
    previewAnswer: {
        fontSize: '13px',
        color: '#6b7280',
        margin: 0
    }
};

export default ProfileEditor;
